import { ServerOff } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ServerUnavailableProps {
  /**
   * Action du bouton principal. Défaut = rechargement complet du navigateur.
   * Injectable pour les stories Storybook (évite de recharger l'iframe au clic).
   */
  onReload?: () => void;
}

/**
 * Page plein écran affichée quand l'app n'arrive pas à joindre le backend Hector
 * au chargement (erreur de catégorie `network` sur la requête de bootstrap
 * `/cabinet-members/me`). Ne couvre PAS le 401 (session expirée), qui garde sa
 * redirection vers `/auth`.
 *
 * Le canal de support pointé est le chat Crisp (icône en bas à droite) : Crisp est
 * un service tiers indépendant du backend Hector, donc joignable même quand notre
 * serveur est down — contrairement à un lien applicatif.
 *
 * Invariant : le chatbox Crisp est autorisé par `AuthProvider`
 * (`syncCrispSupportSession`) sur présence de session auth hors page publique
 * (pas sur la santé backend), et `AuthProvider` est monté AU-DESSUS de `BackendHealthGate` →
 * l'icône est bien présente quand cette page prend le contrôle. Aucune surface
 * de masquage n'est montée ici (dock assistant, panneau bordereau), donc la
 * réconciliation de `useCrisp` le laisse visible. Ne pas déplacer cette
 * autorisation dans `DashboardLayout` (démonté quand la page s'affiche), sinon
 * le canal support disparaîtrait exactement quand on en a besoin.
 *
 * WHY un rechargement complet plutôt qu'un refetch : l'utilisateur a explicitement
 * demandé « recharger la page », et repartir d'un état propre évite tout résidu
 * d'état SPA laissé par l'échec de bootstrap.
 */
const ServerUnavailable = ({
  onReload = () => window.location.reload(),
}: ServerUnavailableProps) => {
  return (
    <div className="flex items-center justify-center min-h-screen px-4 bg-grey-100">
      <div className="flex flex-col items-center text-center gap-4 max-w-md bg-white border border-grey-200 rounded-2xl p-8 sm:p-10 shadow-sm">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-grey-100 text-grey-500">
          <ServerOff className="w-6 h-6" strokeWidth={1.5} />
        </div>

        <h1 className="text-xl font-medium tracking-tight text-charcoal">
          Connexion au serveur impossible
        </h1>

        <p className="text-grey-600 text-sm leading-relaxed">
          Nous n'arrivons pas à joindre le serveur d'Hector. Cela vient le plus
          souvent d'une coupure réseau ou d'un VPN actif.
        </p>

        <ul className="text-left text-sm text-grey-600 space-y-1.5 my-1">
          {[
            "Vérifiez votre connexion internet",
            "Désactivez votre VPN s'il est actif",
            "Rechargez la page",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-grey-400 shrink-0" />
              {item}
            </li>
          ))}
        </ul>

        <Button onClick={onReload} className="mt-1">
          Recharger la page
        </Button>

        <p className="text-xs text-grey-500 leading-relaxed">
          Si le problème persiste, contactez notre support en cliquant sur l'icône
          de chat en bas à droite de votre écran, nous le traiterons au plus vite.
        </p>
      </div>
    </div>
  );
};

export default ServerUnavailable;
